import './polyfills';
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { FpjsProvider } from '@fingerprintjs/fingerprintjs-pro-react';
import { initPostHog, captureError, captureEvent } from './lib/posthog';
import './services/ContentCache'; // Initialize IndexedDB cache for overlay

// BrightSign platform bootstrap — must run FIRST (before initPostHog,
// before any runtime config resolution) so the LS-tier short-circuit can
// redirect the page to `bs-ls.html` before we waste work on heavy
// initializers like PostHog. Guarded by BSDeviceInfo presence so this is
// a zero-cost branch on Android / web-browser paths (the require() never
// loads the platforms/brightsign module in those environments).
if (typeof window !== 'undefined' && typeof window.BSDeviceInfo !== 'undefined') {
  // eslint-disable-next-line global-require
  require('./platforms/brightsign/bootstrap');
}

// BrightSign LS tier short-circuit — the bootstrap above sets
// window.__TRILL_BS_LS_MODE__ when the detected tier is `ls` and
// triggers a navigation to `bs-ls.html`. This flag tells us to skip
// all post-import work: PostHog init, service worker registration,
// error handler attachment, and the React mount. The imports at the
// top of this file (React, ReactDOM, posthog, etc.) are unavoidable
// because CRA does not support dynamic imports on Chrome 51, but the
// redirect to bs-ls.html means a completely separate JS payload will
// be parsed after navigation completes. This branch just avoids
// running _additional_ work on top of the already-parsed modules in
// the small window before navigation starts.
const IS_BRIGHTSIGN_LS_MODE = typeof window !== 'undefined' && window.__TRILL_BS_LS_MODE__ === true;
if (IS_BRIGHTSIGN_LS_MODE) {
  // eslint-disable-next-line no-console
  try { console.log('[Init] BrightSign LS tier detected - skipping React mount, delegating to bs-ls.html'); } catch (e) { /* ignore */ }
}

// Initialize PostHog (kiosk mode - no session recording) — skip on LS
// because the page is redirecting away; we don't want to buffer telemetry
// events in an unload state.
if (!IS_BRIGHTSIGN_LS_MODE) {
  initPostHog();
}

// Expose PostHog key + host for overlay telemetry (uses same build-time secrets as posthog.js)
window.__POSTHOG_KEY__ = process.env.REACT_APP_POSTHOG_KEY || 'phc_FjmLUmNjcfX30IK5u1J6tBRvPaczesCJ02Rc29779kD';
window.__POSTHOG_HOST__ = process.env.REACT_APP_POSTHOG_HOST || 'https://e.trillboards.com';

const normalizeBaseUrl = (value) => {
  if (!value) return null;
  return String(value).trim().replace(/\/+$/, '');
};

const readRuntimeBaseOverrides = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      apiBase: normalizeBaseUrl(params.get('apiBase')),
      appBase: normalizeBaseUrl(params.get('appBase')),
      socketBase: normalizeBaseUrl(params.get('socketBase')),
    };
  } catch (error) {
    return {
      apiBase: null,
      appBase: null,
      socketBase: null,
    };
  }
};

const inferApiBaseFromLocation = () => {
  const { protocol, hostname } = window.location;
  if (hostname === 'india-loop.qa-screen.trillboards.com') {
    return 'https://india-loop.qa-api.trillboards.com';
  }
  if (hostname === 'qa-screen.trillboards.com') {
    return 'https://qa-api.trillboards.com';
  }
  if (hostname === 'screen.trillboards.com') {
    return 'https://api.trillboards.com';
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:4004';
  }
  return `${protocol}//${hostname}`;
};

const inferSocketBaseFromApiBase = (apiBase) => {
  try {
    const apiUrl = new URL(apiBase);
    if (apiUrl.hostname === 'api.trillboards.com') {
      return 'https://chat.trillboards.com';
    }
    return apiUrl.origin;
  } catch (error) {
    return 'https://chat.trillboards.com';
  }
};

const runtimeBaseOverrides = readRuntimeBaseOverrides();

const runtimeApiBase =
  runtimeBaseOverrides.apiBase ||
  normalizeBaseUrl(window.__TRILLBOARDS_API_BASE) ||
  normalizeBaseUrl(process.env.REACT_APP_API_URL || process.env.REACT_APP_APP_URL) ||
  inferApiBaseFromLocation();

const runtimeSocketBase =
  runtimeBaseOverrides.socketBase ||
  normalizeBaseUrl(window.__TRILLBOARDS_SOCKET_BASE) ||
  normalizeBaseUrl(process.env.REACT_APP_SOCKET_URL) ||
  inferSocketBaseFromApiBase(runtimeApiBase);

window.__TRILLBOARDS_API_BASE = runtimeApiBase;
window.__TRILLBOARDS_APP_BASE =
  runtimeBaseOverrides.appBase ||
  normalizeBaseUrl(process.env.REACT_APP_APP_URL) ||
  runtimeApiBase;
window.__TRILLBOARDS_SOCKET_BASE = runtimeSocketBase;
window.__TRILLBOARDS_RUNTIME_BASES__ = {
  apiBase: runtimeApiBase,
  appBase: window.__TRILLBOARDS_APP_BASE,
  socketBase: runtimeSocketBase,
};

const PROD_API_ORIGIN = 'https://api.trillboards.com';

const rewriteRuntimeApiUrl = (value) => {
  try {
    const resolved = new URL(typeof value === 'string' ? value : value.toString(), window.location.origin);
    if (resolved.origin !== PROD_API_ORIGIN || runtimeApiBase === PROD_API_ORIGIN) {
      return value;
    }

    const runtimeUrl = new URL(runtimeApiBase);
    resolved.protocol = runtimeUrl.protocol;
    resolved.host = runtimeUrl.host;
    return resolved.toString();
  } catch (error) {
    return value;
  }
};

if (
  typeof window !== 'undefined' &&
  typeof window.fetch === 'function' &&
  runtimeApiBase !== PROD_API_ORIGIN &&
  !window.__TRILLBOARDS_FETCH_PATCHED__
) {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    let nextInput = input;

    if (typeof input === 'string' || input instanceof URL) {
      nextInput = rewriteRuntimeApiUrl(input);
    } else if (typeof Request !== 'undefined' && input instanceof Request) {
      const rewrittenUrl = rewriteRuntimeApiUrl(input.url);
      if (typeof rewrittenUrl === 'string' && rewrittenUrl !== input.url) {
        nextInput = new Request(rewrittenUrl, input);
      }
    }

    return originalFetch(nextInput, init);
  };
  window.__TRILLBOARDS_FETCH_PATCHED__ = true;
}

// CTV context detection (Android WebView / TV platforms)
// More lenient detection: treat TV platforms as CTV even without fingerprint param
// This prevents aggressive desktop reload logic on TVs that may have lost their fingerprint
function detectCtvContext() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const hasFingerprint = urlParams.has('fingerprint') || urlParams.has('fp');
    const forceNoSw = urlParams.get('nosw') === '1' || urlParams.get('no_sw') === '1';
    const platform = String(urlParams.get('platform') || '').trim().toLowerCase();
    const fingerprint = String(urlParams.get('fingerprint') || urlParams.get('fp') || '').trim();
    const ua = navigator.userAgent.toLowerCase();

    const isBrightSign =
      platform === 'brightsign' ||
      window.__TRILL_PLATFORM__ === 'brightsign' ||
      typeof window.BSDeviceInfo !== 'undefined' ||
      /^brightsign-/i.test(fingerprint) ||
      ua.includes('brightsign');

    // Explicit no-service-worker mode from native wrappers
    if (forceNoSw) {
      return true;
    }

    // BrightSign packages provide a stable URL fingerprint and run in an
    // embedded player browser, so keep them on the native/CTV path. This
    // avoids loading browser-only providers such as FingerprintJS Pro.
    if (isBrightSign) {
      return true;
    }

    // TV platform detection (independent of fingerprint)
    // AFT[A-Z] matches all Fire TV models (AFTB, AFTS, AFTT, AFTM, AFTR, AFTKRT, etc.)
    const isTV = ua.includes('tv') || ua.includes('silk') ||
      /aft[a-z]/i.test(ua) ||  // All Fire TV model codes (AFT followed by any letter)
      ua.includes('firetv') || ua.includes('roku') || ua.includes('tizen') ||
      ua.includes('webos') || ua.includes('crkey') || ua.includes('chromecast') ||
      platform === 'brightsign';

    // Always treat TV platforms as CTV (lenient - prevents aggressive reloads on TVs)
    // This is important: TVs may lose fingerprint param but shouldn't get desktop behavior
    if (isTV) {
      return true;
    }

    // Android WebView with fingerprint = CTV (app-embedded screens)
    const isAndroidWebView = ua.includes('wv') || (ua.includes('android') && !ua.includes('chrome/'));
    if (hasFingerprint && isAndroidWebView) {
      return true;
    }

    // Also treat Android devices with fingerprint as CTV (tablet kiosks)
    if (hasFingerprint && ua.includes('android')) {
      return true;
    }

    return false;
  } catch (e) {
    return false;
  }
}

const IS_CTV_CONTEXT = detectCtvContext();
const FORCE_NO_SW = (() => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('nosw') === '1' || urlParams.get('no_sw') === '1';
  } catch (e) {
    return false;
  }
})();
try {
  window.__TRILL_IS_CTV__ = IS_CTV_CONTEXT;
} catch (e) {
  // Ignore if window is locked down
}

// All screens at screen.trillboards.com are kiosk contexts — pre-activate for IMA SDK.
// PR #1129 stopped spoofing input events for wake lock, which removed the side effect
// of triggering the overlay's user activation tracker. Without this, IMA SDK defers
// ad requests indefinitely waiting for a human gesture that never comes on kiosks.
try {
  if (!window.__TRILL_USER_ACTIVATION_TS__) {
    window.__TRILL_USER_ACTIVATION_TS__ = Date.now();
    window.__TRILL_USER_ACTIVATION_SOURCE__ = 'kiosk-auto';
    captureEvent('kiosk_auto_activation', {
      is_ctv: IS_CTV_CONTEXT,
      ua: navigator.userAgent?.substring(0, 120),
      url: window.location.href
    });
  }
} catch (e) {
  // Ignore if window is locked down
}

// ============================================================
// Error Classification
// Distinguishes critical JavaScript errors from expected/non-critical failures
// ============================================================
function classifyError(error, source) {
  const message = error?.message || String(error);
  const name = error?.name || '';
  const stack = error?.stack || '';
  const combined = `${name} ${message} ${stack}`;

  // Non-critical: Expected failures that shouldn't trigger reload
  const nonCriticalPatterns = [
    /NotAllowedError/i,           // WakeLock visibility, autoplay blocked
    /NotSupportedError.*WakeLock/i, // WakeLock not supported
    /ServiceWorker/i,             // SW registration/update
    /service.worker/i,
    /wakeLock/i,
    /autoplay/i,                  // Video autoplay blocked
    /play\(\) failed/i,
    /AbortError.*play/i,
    /The play\(\) request was interrupted/i,
    /user denied permission/i,    // Fullscreen, notification permissions
    /IMA/i,                       // IMA ad errors (expected)
    /ima3?\.js/i,
    /googletag/i,
    /gpt\.js/i,
    /prebid/i,
    /FingerprintJS/i,
    /fpnpmcdn\.net/i,
    /Subscription not active/i,
    /ResizeObserver loop/i,       // Browser quirk, not real error
    /Script error\./i,            // Cross-origin errors (no detail)
    /Loading chunk .* failed/i,   // Network issues, will retry
    /Failed to fetch/i,           // Network offline
    /NetworkError/i,
    /Load failed/i,
    /timeout/i,
    /CORS/i,
  ];

  for (const pattern of nonCriticalPatterns) {
    if (pattern.test(combined)) {
      return 'non_critical';
    }
  }

  // Critical: Actual JavaScript errors that indicate bugs
  const criticalTypes = ['TypeError', 'ReferenceError', 'SyntaxError', 'RangeError'];
  if (criticalTypes.includes(name)) {
    return 'critical';
  }

  // EvalError, URIError, InternalError - also critical
  if (name.endsWith('Error') && !['NotAllowedError', 'AbortError', 'NetworkError', 'NotSupportedError'].includes(name)) {
    return 'critical';
  }

  // Default: unknown, count but be lenient
  return 'unknown';
}

// ============================================================
// Global Error Handler with Auto-Recovery
// Prevents silent crashes and implements intelligent reload strategy
// ============================================================
const ERROR_WINDOW_MS = IS_CTV_CONTEXT ? 120000 : 30000; // 30s default, 2m on CTV
const MAX_ERRORS_BEFORE_RELOAD = IS_CTV_CONTEXT ? 10 : 3;
const CRASH_LOOP_KEY = 'trillboards_reload_count';
const CRASH_LOOP_TIMESTAMP_KEY = 'trillboards_reload_timestamp';
const MAX_RELOADS_BEFORE_HELP = IS_CTV_CONTEXT ? 5 : 3;
const RELOAD_WINDOW_MS = 300000; // 5 minutes

// Track errors for reload decision (capped at 100 entries to prevent memory leaks)
const MAX_ERROR_TIMESTAMPS = 100;
let errorTimestamps = [];

function shouldReload() {
  const now = Date.now();
  // Filter to only errors in the last ERROR_WINDOW_MS
  errorTimestamps = errorTimestamps.filter(ts => now - ts < ERROR_WINDOW_MS);
  // Cap the array to prevent unbounded growth
  if (errorTimestamps.length > MAX_ERROR_TIMESTAMPS) {
    errorTimestamps = errorTimestamps.slice(-MAX_ERROR_TIMESTAMPS);
  }
  return errorTimestamps.length >= MAX_ERRORS_BEFORE_RELOAD;
}

function isInCrashLoop() {
  try {
    const reloadCount = parseInt(sessionStorage.getItem(CRASH_LOOP_KEY) || '0', 10);
    const lastReloadTimestamp = parseInt(sessionStorage.getItem(CRASH_LOOP_TIMESTAMP_KEY) || '0', 10);
    const now = Date.now();

    // Reset if outside the reload window
    if (now - lastReloadTimestamp > RELOAD_WINDOW_MS) {
      sessionStorage.setItem(CRASH_LOOP_KEY, '0');
      return false;
    }

    return reloadCount >= MAX_RELOADS_BEFORE_HELP;
  } catch (e) {
    return false;
  }
}

function incrementReloadCount() {
  try {
    const lastTimestamp = parseInt(sessionStorage.getItem(CRASH_LOOP_TIMESTAMP_KEY) || '0', 10);
    const now = Date.now();

    // Reset count if outside window
    if (now - lastTimestamp > RELOAD_WINDOW_MS) {
      sessionStorage.setItem(CRASH_LOOP_KEY, '1');
    } else {
      const currentCount = parseInt(sessionStorage.getItem(CRASH_LOOP_KEY) || '0', 10);
      sessionStorage.setItem(CRASH_LOOP_KEY, String(currentCount + 1));
    }
    sessionStorage.setItem(CRASH_LOOP_TIMESTAMP_KEY, String(now));
  } catch (e) {
    // Ignore storage errors
  }
}

function showNeedsHelpScreen() {
  // Display a "device needs help" screen instead of crash looping
  const helpScreen = document.createElement('div');
  helpScreen.id = 'trillboards-needs-help';
  helpScreen.innerHTML = `
    <div style="
      position: fixed;
      top: 0; left: 0; right: 0; bottom: 0;
      background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: white;
      text-align: center;
      z-index: 999999;
    ">
      <div style="font-size: 64px; margin-bottom: 24px;">⚠️</div>
      <h1 style="font-size: 32px; margin: 0 0 16px 0; font-weight: 600;">Screen Needs Attention</h1>
      <p style="font-size: 18px; opacity: 0.8; max-width: 400px; line-height: 1.5;">
        This screen has encountered repeated errors. Please restart the device or contact support.
      </p>
      <p style="font-size: 14px; opacity: 0.5; margin-top: 24px;">
        Screen ID: ${localStorage.getItem('screenId') || 'Unknown'}
      </p>
      <button onclick="sessionStorage.clear(); window.location.reload();" style="
        margin-top: 32px;
        padding: 16px 32px;
        font-size: 18px;
        background: #4f46e5;
        border: none;
        border-radius: 8px;
        color: white;
        cursor: pointer;
      ">Try Again</button>
    </div>
  `;
  document.body.appendChild(helpScreen);
}

function handleGlobalError(error, source = 'unknown') {
  const errorClass = classifyError(error, source);

  const errorInfo = {
    message: error?.message || String(error),
    stack: error?.stack,
    name: error?.name,
    source,
    error_class: errorClass,  // Classification for PostHog analysis
    is_ctv: IS_CTV_CONTEXT,   // CTV flag for filtering
    url: window.location.href,
    timestamp: new Date().toISOString(),
    fingerprint: localStorage.getItem('fingerprint'),
    screenId: localStorage.getItem('screenId')
      || window.__TRILLBOARD_SCREEN_ID__
      || window.myId
      || null
  };

  console.error('[GlobalError]', source, errorClass, errorInfo);

  // Only track critical/unknown errors in PostHog — skip non-critical noise
  // (WakeLock, ServiceWorker, IMA, autoplay, network, etc.)
  if (errorClass !== 'non_critical') {
    try {
      captureError(error, { source, error_class: errorClass, is_ctv: IS_CTV_CONTEXT });
    } catch (e) {
      // Ignore PostHog errors
    }
  }

  // Only count CRITICAL errors toward reload threshold
  // Non-critical errors (WakeLock, SW updates, IMA, etc.) shouldn't trigger reloads
  if (errorClass !== 'critical') {
    console.info(`[GlobalError] Non-critical error (${errorClass}), not counting toward reload threshold`);
    return; // Don't trigger reload logic for non-critical errors
  }

  // Add to error count (only critical errors reach here)
  errorTimestamps.push(Date.now());

  // Check if we should reload
  if (shouldReload()) {
    if (IS_CTV_CONTEXT) {
      console.warn('[GlobalError] CTV context - attempting soft recovery instead of reload');
      try {
        if (window.minimalStreamManager?.forceReloadCurrentStream) {
          window.minimalStreamManager.forceReloadCurrentStream('global_error');
        } else if (window.minimalStreamManager?.fetchAndUpdateStream) {
          window.minimalStreamManager.fetchAndUpdateStream(true);
        }
        if (window.minimalStreamManager?.kickActiveMedia) {
          window.minimalStreamManager.kickActiveMedia();
        }
      } catch (e) {
        // Ignore recovery errors
      }
      return;
    }
    if (isInCrashLoop()) {
      console.error('[GlobalError] Crash loop detected - showing help screen');
      showNeedsHelpScreen();
    } else {
      console.warn('[GlobalError] Too many critical errors - reloading page');
      incrementReloadCount();
      // Delay reload slightly to allow error logging to complete
      // But wait for IMA ad to finish if playing
      const scheduleReload = () => {
        const isShowingImaAd = window.minimalAdvertisementOverlay?.isShowingImaAd;
        if (isShowingImaAd) {
          console.log('[GlobalError] IMA ad playing, deferring reload for 5s');
          setTimeout(scheduleReload, 5000);
          return;
        }
        window.location.reload();
      };
      setTimeout(scheduleReload, 1000);
    }
  }
}

// Global error handlers
window.addEventListener('error', (event) => {
  handleGlobalError(event.error || event.message, 'window.onerror');
});

window.addEventListener('unhandledrejection', (event) => {
  handleGlobalError(event.reason, 'unhandledrejection');
});

// Reset crash loop counter on successful load after 60 seconds
setTimeout(() => {
  try {
    sessionStorage.setItem(CRASH_LOOP_KEY, '0');
    console.log('[GlobalError] App stable for 60s - reset crash loop counter');
  } catch (e) {
    // Ignore
  }
}, 60000);

// Track interval IDs for cleanup
let swUpdateIntervalId = null;

// Register Service Worker for offline capability (skip on CTV/WebView
// and on BrightSign LS — LS is redirecting to bs-ls.html which has its
// own simplified cache strategy).
if ('serviceWorker' in navigator && !IS_CTV_CONTEXT && !FORCE_NO_SW && !IS_BRIGHTSIGN_LS_MODE) {
    window.addEventListener('load', () => {
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (refreshing) return;
            refreshing = true;
            // Wait for IMA ad to finish if playing
            const scheduleReload = () => {
                const isShowingImaAd = window.minimalAdvertisementOverlay?.isShowingImaAd;
                if (isShowingImaAd) {
                    console.log('[SW] IMA ad playing, deferring reload for 5s');
                    setTimeout(scheduleReload, 5000);
                    return;
                }
                window.location.reload();
            };
            scheduleReload();
        });

        navigator.serviceWorker.register('/sw.js')
            .then(registration => {
                console.log('[SW] Service Worker registered:', registration.scope);

                // Check for updates periodically (store interval ID for cleanup)
                swUpdateIntervalId = setInterval(() => {
                    registration.update();
                }, 60000); // Check every minute

                // Handle updates
                registration.addEventListener('updatefound', () => {
                    const newWorker = registration.installing;
                    if (newWorker) {
                        newWorker.addEventListener('statechange', () => {
                            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                console.log('[SW] New version available, will activate on next load');
                            }
                        });
                    }
                });
            })
            .catch(error => {
                console.warn('[SW] Service Worker registration failed:', error);
                // Don't fail the app if SW registration fails (e.g., older TV browsers)
            });
    });
} else {
    if (IS_CTV_CONTEXT || FORCE_NO_SW) {
      console.log('[SW] Skipping service worker registration for CTV/no-SW context');
    } else {
      console.log('[SW] Service Workers not supported in this browser');
    }
}

// Cleanup intervals on page unload to prevent memory leaks
window.addEventListener('beforeunload', () => {
  if (swUpdateIntervalId) {
    clearInterval(swUpdateIntervalId);
    swUpdateIntervalId = null;
  }
});

// Unregister any existing Service Workers on CTV/WebView to prevent update errors
// Previously-registered SWs still try to update themselves and fail on these devices
if ('serviceWorker' in navigator && (IS_CTV_CONTEXT || FORCE_NO_SW)) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister().then(success => {
        if (success) {
          console.log('[SW] Unregistered existing SW for CTV context');
        }
      }).catch(() => {});
    });
  }).catch(() => {});
}

// Mount React — skipped entirely on BrightSign LS tier because the
// browser is already redirecting to bs-ls.html, so any work here is
// wasted heap pressure.
if (!IS_BRIGHTSIGN_LS_MODE) {
  const root = ReactDOM.createRoot(document.getElementById('root'));

  // Skip FpjsProvider on CTV/WebView devices - they use open-source FingerprintJS in QRCode.js
  // FpjsProvider's CDN script doesn't work on older WebViews (Android 7.0)
  if (IS_CTV_CONTEXT) {
    console.log('[Init] CTV context detected - rendering without FpjsProvider');
    root.render(<App />);
  } else {
    root.render(
      <FpjsProvider loadOptions={{
          apiKey: "BwcpDekjRdgI3jp0m9EM",
          region: process.env.REACT_APP_FPJS_REGION || 'us'
      }}>
      <App />
      </FpjsProvider>
    );
  }
  reportWebVitals();
}
